import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import * as Yup from "yup";
import { toast } from "react-toastify";

import { ListProperties, NewPropertyForm } from "../../component";
import { Enumeration, ListProperty, SearchProperty, StatusItem } from "../../utils/interface";
import Api from "../../Api";

const FindProperty = () => {
  const api = Api();

  const [propertiesList, setPropertiesList] = useState<ListProperty[]>([]);
  const [propertiesListCount, setPropertiesListCount] = useState<number>(0);
  const [propertiesListPageIndex, setPropertiesListPageIndex] = useState<number>(0);
  const [propertiesListPageSize, setPropertiesListPageSize] = useState<number>(10);
  const initialState: SearchProperty = {
    propertyId: undefined,
    propertyTypeEnumId: undefined,
    isVerified: undefined,
    propertyTitle: '',
    ownerId: undefined,
    statusId: undefined,
    createdAt: '',
  }
  const [findOptions, setFindOptions] = useState<SearchProperty>(initialState);
  const [propertyStatuses, setPropertyStatuses] = useState<StatusItem[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<Enumeration[]>([]);
  const initialComponentName = 'ListProperties';
  const [componentName, setComponentName] = useState(initialComponentName);

  const navigateToNewPropertyForm = () => setComponentName('NewPropertyForm');
  const navigateToListProperties = () => setComponentName(initialComponentName);
  const onChangePageIndex = (pageNumber: number) => setPropertiesListPageIndex((pageNumber - 1));
  const onChangePageSize = (pageSize: number) => setPropertiesListPageSize(pageSize);
  const resetFindOptions = () => setFindOptions(initialState);
  const handleFindProperty = (findOptions: SearchProperty) => setFindOptions(findOptions);

  const PropertyValidationSchema = Yup.object().shape({
    propertyTypeEnumId: Yup.number().required("Property Type is required"),
    propertyTitle: Yup.string().required("Title is required"),
    ownerId: Yup.number().required("Please select an owner"),
    mobile: Yup.string()
      .matches(/^[0]?[6789]\d{9}$/, 'Invalid mobile number')
      .required('Mobile is required'),
    addressLine1: Yup.string().required("Address 01 is required"),
    locality: Yup.string().required("Locality is required"),
    city: Yup.string().required("Please select a city"),
    stateISOCode: Yup.string().required("Please select a state"),
    state: Yup.string().required("Please select a state"),
    pincode: Yup.string().required("Pincode is required"),
  });

  const searchProperty = (findOptions: SearchProperty) => {
    api.property.searchProperty(findOptions)
      .then((response: any) => {
        const data = response.result.data;
        setPropertiesList(data.propertiesList);
        setPropertiesListCount(data.propertiesListCount);
      })
  }
  const fetchOwnersForDropdown = async (searchTerm: string, pageIndex: number, pageSize: number) => {
    const response = await api.user.searchCustomer({
      searchTerm,
      pageIndex,
      pageSize,
    });
    const usersListCount = response.result.data.usersListCount;

    const hasMore = (pageIndex + 1) * pageSize < usersListCount;

    return {
      options: response.result.data.usersList.map((user: any) => ({
        value: user.userId,
        label: `${user.userId}: ${user.fullName}`,
      })),
      hasMore,
    };
  }
  const getPropertyStatuses = () => {
    api.property.getStatuses()
      .then((response: any) => {
        const statusItems: StatusItem[] = response.result.data;
        setPropertyStatuses(statusItems);
      })
  };
  const getPropertyTypes = () => {
    api.property.getPropertyTypes()
      .then((response: any) => {
        const types: Enumeration[] = response.result.data;
        setPropertyTypes(types);
      })
  };
  const savePropertyLocation = (propertyId: number, propertyLocationValues: any) => {
    api.property.savePropertyLocation(propertyId, propertyLocationValues)
    .then((response: any) => {
      toast.success(response.result.message);
      navigateToListProperties();
      resetFindOptions();
      searchProperty({
        pageIndex: propertiesListPageIndex,
        pageSize: propertiesListPageSize,
      });
    })
  };
  const createProperty = (propertyDetails: any) => {
    api.property.createProperty(propertyDetails)
      .then((response: any) => {
        toast.success(response.result.message);
        savePropertyLocation(response.result.data.propertyId, propertyDetails);
      })
  };

  const handleDeleteProperty = (propertyId: number) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      api.property.deleteProperty(propertyId)
        .then((response: any) => {
          toast.success(response.result.message);
          searchProperty({
            ...findOptions,
            pageIndex: propertiesListPageIndex,
            pageSize: propertiesListPageSize,
          });
        })
        .catch((err: any) => {
          toast.error(err.response?.data?.message || "Failed to delete property");
        });
    }
  };

  useEffect(() => {
    searchProperty({
      ...findOptions,
      pageIndex: propertiesListPageIndex,
      pageSize: propertiesListPageSize
    });
  }, [propertiesListPageIndex, propertiesListPageSize, findOptions])
  useEffect(() => {
    getPropertyStatuses();
    getPropertyTypes();
  }, [])

  return (<>
    {componentName === initialComponentName ? (<>
      <Button
        variant="primary"
        className="ml-1 mb-3"
        onClick={navigateToNewPropertyForm}>
        Create New Property
      </Button>
      <ListProperties
        propertiesList={propertiesList}
        propertiesListCount={propertiesListCount}
        propertiesListPageIndex={propertiesListPageIndex}
        propertiesListPageSize={propertiesListPageSize}
        onChangePageIndex={onChangePageIndex}
        onChangePageSize={onChangePageSize}
        findOptions={findOptions}
        handleFindProperty={handleFindProperty}
        resetFindOptions={resetFindOptions}
        fetchOwners={fetchOwnersForDropdown}
        propertyStatuses={propertyStatuses}
        propertyTypes={propertyTypes}
        onDeleteProperty={handleDeleteProperty}
      />
    </>) : (<>
      <Button variant="primary" onClick={navigateToListProperties}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back
      </Button>
      <NewPropertyForm
        fetchOwners={fetchOwnersForDropdown}
        propertyTypes={propertyTypes}
        PropertyValidationSchema={PropertyValidationSchema}
        createNewProperty={createProperty}
      />
    </>)}
  </>);
};

export default FindProperty;
